import React, { useState, useEffect } from 'react';
import { CheckIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { LoadingSkeleton, ErrorState } from '@repro/ui';
import { Invoice } from '../shared';
import { api } from '../utils/api';

interface InvoiceDetailProps {
  organisationId: string;
  invoiceId: string;
}

interface StepDefinition {
  title: string;
  description: string;
  state: 'finish' | 'process' | 'error';
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'pending':
    case 'sent':
      return 'bg-yellow-100 text-yellow-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'draft':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
    case 'refunded':
      return 'bg-base-200 text-base-content/70';
    default:
      return 'bg-base-200 text-base-content/70';
  }
}

function formatDate(value: Date | string | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

function getPaymentSteps(status: string): StepDefinition[] {
  const steps: StepDefinition[] = [
    { title: 'Draft', description: 'Invoice created', state: 'finish' },
    { title: 'Sent', description: 'Invoice sent to customer', state: 'finish' },
  ];

  if (status === 'paid') {
    steps.push({ title: 'Paid', description: 'Payment received', state: 'finish' });
  } else if (status === 'overdue') {
    steps.push({ title: 'Overdue', description: 'Payment overdue', state: 'error' });
  } else {
    steps.push({ title: 'Pending', description: 'Awaiting payment', state: 'process' });
  }

  return steps;
}

const InvoiceDetail: React.FC<InvoiceDetailProps> = ({ organisationId, invoiceId }) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getInvoice(organisationId, invoiceId)
      .then((data) => {
        if (!cancelled) setInvoice(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load invoice');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId, invoiceId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (error || !invoice) {
    return <ErrorState title="Couldn't load invoice" body={error ?? 'Invoice not found'} />;
  }

  const steps = getPaymentSteps(invoice.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-base-content">Invoice #{invoice.invoiceNumber}</h1>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(
              invoice.status
            )}`}
          >
            {invoice.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Invoice details */}
        <Card title="Invoice Details">
          <Row label="Issue Date" value={formatDate(invoice.issueDate)} />
          <Row label="Due Date" value={formatDate(invoice.dueDate)} />
          <Row label="Currency" value={invoice.currency} />
          {invoice.purchaseOrderNumber && (
            <Row label="PO Number" value={invoice.purchaseOrderNumber} />
          )}
          <Row label="Subtotal" value={`£${invoice.subtotal.toFixed(2)}`} />
          <Row label="Tax Total" value={`£${invoice.taxTotal.toFixed(2)}`} />
          <Row
            label="Total Amount"
            value={<span className="font-semibold">£{invoice.total.toFixed(2)}</span>}
          />
        </Card>

        {/* Billing address */}
        <Card title="Billing Address">
          <p className="font-semibold text-base-content">{invoice.billingAddress.companyName}</p>
          {invoice.billingAddress.contactName && (
            <p className="text-sm text-base-content/80">{invoice.billingAddress.contactName}</p>
          )}
          <p className="text-sm text-base-content/80">{invoice.billingAddress.addressLine1}</p>
          {invoice.billingAddress.addressLine2 && (
            <p className="text-sm text-base-content/80">{invoice.billingAddress.addressLine2}</p>
          )}
          <p className="text-sm text-base-content/80">
            {invoice.billingAddress.city}, {invoice.billingAddress.postalCode}
          </p>
          <p className="text-sm text-base-content/80">{invoice.billingAddress.country}</p>
          {invoice.billingAddress.vatNumber && (
            <p className="text-sm text-base-content/70 mt-2">
              <span className="font-medium">VAT Number:</span> {invoice.billingAddress.vatNumber}
            </p>
          )}
        </Card>
      </div>

      {/* Line items */}
      <Card title="Line Items">
        {invoice.lineItems && invoice.lineItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-right">Quantity</th>
                  <th className="text-right">Unit Price</th>
                  <th className="text-right">Tax Rate</th>
                  <th className="text-right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.description}</td>
                    <td className="text-right">{item.quantity}</td>
                    <td className="text-right">£{item.unitPrice.toFixed(2)}</td>
                    <td className="text-right">
                      {item.taxRate !== undefined ? `${(item.taxRate * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="text-right font-medium">£{item.lineTotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-base-content/60">No line items.</p>
        )}
      </Card>

      {/* Payment status timeline */}
      <Card title="Payment Status Timeline">
        <Stepper steps={steps} />
      </Card>
    </div>
  );
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-base-100 shadow rounded-lg p-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/60 mb-3">
        {title}
      </h2>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-base-content/70">{label}</span>
      <span className="text-base-content text-right">{value}</span>
    </div>
  );
}

function Stepper({ steps }: { steps: StepDefinition[] }) {
  return (
    <ol className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0">
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        return (
          <li key={step.title} className="flex sm:flex-col sm:items-start gap-3 sm:flex-1">
            <div className="flex sm:flex-col items-center sm:items-start gap-2 sm:gap-0 shrink-0">
              <StepIcon state={step.state} />
              {!isLast && (
                <div
                  className={`hidden sm:block h-px w-full -mt-3 ${
                    step.state === 'finish' ? 'bg-green-500' : 'bg-base-300'
                  }`}
                />
              )}
            </div>
            <div className="sm:mt-2">
              <p className={`text-sm font-medium ${stepTitleClass(step.state)}`}>{step.title}</p>
              <p className="text-xs text-base-content/60">{step.description}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function StepIcon({ state }: { state: StepDefinition['state'] }) {
  if (state === 'finish') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white">
        <CheckIcon className="h-4 w-4" />
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white">
        <ExclamationTriangleIcon className="h-4 w-4" />
      </span>
    );
  }
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-yellow-500 text-white">
      <ClockIcon className="h-4 w-4" />
    </span>
  );
}

function stepTitleClass(state: StepDefinition['state']): string {
  switch (state) {
    case 'finish':
      return 'text-base-content';
    case 'error':
      return 'text-error';
    case 'process':
      return 'text-base-content';
  }
}

export default InvoiceDetail;
